/* eslint-disable max-len */
import Vue from 'vue';
import convert from 'convert-units';
import moment from 'moment';
import Activity from '../lib/Activity';
import { calendarDateRange, camelizeKeys } from '../lib/functions';
import {
  getters as analyticsGetters,
  actions as analyticsActions,
  mergeCalendar,
} from './calendar/analytics';
import Workout from '../lib/workout/Workout';
import kpis from './calendar/kpis';
import {
  monday,
  cloneDay,
  calendarSort, indexForDate, cloneCalendar, oneWeekAgo, today, fillGaps,
} from './calendar/functions';
import {
  plannerCalendarGetters,
  plannerCalendarMutations,
  plannerCalendarActions, plannerCalendarState,
} from './calendar/plannerCalendar';
import {
  powerScalesActions,
  powerScalesGetters,
  powerScalesMutations,
  powerScalesState,
} from './calendar/powerScales';

const acrNext = (getters, key) => {
  if ((((getters.today || {}).activities) || []).length === 0
    && (((getters.today || {}).workouts || []).length > 0)) {
    return getters.today[key];
  }

  if (getters.tomorrow && (getters.tomorrow.workouts || []).length > 0) {
    return getters.tomorrow[key];
  }

  return null;
};

const addActivityToDay = (day, activity) => {
  const d = day;
  d.activities = day.activities || [];
  d.activities.push(activity);
  d.distance = (day.distance || 0) + (activity.totalDistance || 0);
  d.duration = (day.duration || 0) + (activity.duration || 0);
  d.weightedLoad = (day.weightedLoad || 0) + (activity.weightedLoad || 0);

  return d;
};

export default {
  state: {
    ...plannerCalendarState,
    ...powerScalesState,
    calendars: {},
    averagePowerScales: {},
    activeCalendar: null,
  },
  getters: {
    ...analyticsGetters,
    ...kpis,
    ...plannerCalendarGetters,
    ...powerScalesGetters,
    activeCalendar: state => state.activeCalendar,
    activeMember: (state, getters) => getters.activeCalendar,
    calendar: state => state.calendars[state.activeCalendar],
    calendars: state => state.calendars,
    calendarCopy: (state, getters) => cloneCalendar(getters.calendar || []),
    day: (state, getters) => date => (getters.calendar || [])[indexForDate(date, getters.calendar)],
    today: (state, getters) => (getters.calendar || []).find(d => d.date.isSame(moment(), 'day')),
    tomorrow: (state, getters) => (getters.calendar || []).find(d => d.date.isSame(moment().add(1, 'day'), 'day')),
    metric: (state, getters) => (metric, start, end, forDf) => (getters.calendar || []).filter(
      d => (!start || d.date.isSameOrAfter(start)) && (!end || d.date.isSameOrBefore(end)),
    ).map(d => forDf ? d[metric] : { date: d.date, [metric]: d[metric] }),
    activities: (state, getters) => (getters.calendar || [])
      .reduce((activities, day) => {
        if ('activities' in day) {
          activities.push(...day.activities.map(d => new Activity(d)));
        }
        return activities;
      }, [])
      .sort(calendarSort),
    workouts: (state, getters) => (getters.calendar || [])
      .reduce((workouts, day) => {
        if ('workouts' in day) {
          const tps = day.workouts
            .filter(tp => !tp.removedOn)
            .map(d => new Workout(d));
          if (tps.length) {
            workouts.push(...tps);
          }
        }
        return workouts;
      }, [])
      .sort(calendarSort),

    consistency: (state, getters) => (getters.calendar || [])
      .filter(d => d.date.isBetween(moment().subtract(30, 'days'), moment().endOf('day')))
      .map(d => ({
        date: d.date.toDate(),
        value: d.consistency,
      }))
      .sort((a, b) => a.date - b.date),
    overallFitness: (state, getters) => (getters.calendar || [])
      .filter(d => d.date.isBetween(moment().subtract(30, 'days'), moment().endOf('day')))
      .map(d => ({
        date: d.date.toDate(),
        value: d.ebChronicLoad,
      }))
      .sort((a, b) => a.date - b.date),
    intensity: (state, getters) => getters.powerCurve(moment().subtract(30, 'days'), moment()),
    powerCurve: (state, getters) => (start, end) => {
      const times = ['w5sec', 'w15sec', 'w30sec', 'w1min', 'w3min', 'w5min', 'w9min', 'w10min', 'w20min', 'w30min', 'w1hr'];
      const thirty = times.map(
        time => Math.max(...getters.metric(time, start, end).map(i => i[time])),
      );
      const wtd = times.map(
        time => Math.max(...getters.metric(time, oneWeekAgo(), today()).map(i => i[time])),
      );
      return times.map((time, index) => ({
        x: time.replace(/^w/, ''),
        thirty: thirty[index],
        wtd: wtd[index],
      }));
    },

    nextPlannedActivity: (state, getters) => getters.workouts.find(tp => tp.localStartTime.isSameOrAfter(moment().startOf('day'))),
    hasPlannedActivity: (state, getters) => getters.nextPlannedActivity !== undefined
      && getters.nextPlannedActivity.localStartTime.isBetween(
        moment().startOf('day'),
        moment().add(1, 'day').endOf('day'),
        null,
        '[]',
      ),
    latestActivity: (state, getters) => getters.activities.slice(-1).pop(),
    trainedToday: (state, getters) => getters.latestActivity && moment(getters.latestActivity.localStartTime).isSame(moment(), 'day'),
    criticalPowerNow: (state, getters) => {
      let d = getters.today;
      if (!d) {
        return 0;
      }
      while (d.criticalPower <= 0) {
        d = getters.day(d.date.clone().subtract(1, 'day'));
      }
      return d.criticalPower;
    },
    vbAcrNow: (state, getters) => (getters.today || {}).vbAcr,
    ebAcrNow: (state, getters) => (getters.today || {}).ebAcr,
    vbAcrNext: (state, getters) => acrNext(getters, 'vbAcrPw'),
    ebAcrNext: (state, getters) => acrNext(getters, 'ebAcrPw'),

    twoWeekTrainingConsistency: (state, getters) => (endDate, includeWorkouts) => Math.round(
      getters.calendar.filter(d => d.date.isBetween(endDate.clone().subtract(2, 'weeks'), endDate, '[]'))
        .reduce((a, b) => (a + (b.numActivities > 0 || (includeWorkouts && b.numWorkouts > 0) ? 1 : 0)), 0),
    ),

    diaryCommentsToday: (state, getters) => (getters.today || {}).journalEntries || [],
    hasCommentedToday: (state, getters) => getters.diaryCommentsToday.length > 0,
    havePreWorkoutInput: (state, getters) => (date) => {
      const day = getters.day(date || moment());
      return day
        && day.subjectiveInput
        && day.subjectiveInput.hoursSlept !== null
        && day.subjectiveInput.motivation !== null;
    },
    hasData: (state, getters) => getters.activities.length > 0,
    hasDataInLast30Days: (state, getters) => getters.activities
      .filter(a => a.localStartTime.isAfter(moment().subtract(30, 'days'))).length > 0,
  },
  mutations: {
    ...plannerCalendarMutations,
    ...powerScalesMutations,
    clearCalendar(state) {
      Vue.set(state, 'calendars', {});
      Vue.set(state, 'activeCalendar', null);
    },
    loadCalendar(state, { memberId, calendar }) {
      Vue.set(state.calendars, memberId, calendar.map((d) => {
        const date = camelizeKeys(d);
        date.date = moment(d.date);
        return date;
      }));
      Vue.set(state, 'activeCalendar', memberId);
    },
    updateCalendar(state, data) {
      const calendar = mergeCalendar(state.calendars[state.activeCalendar], data);
      Vue.set(state.calendars, state.activeCalendar, calendar);
      // For some reason, this triggers any observers watching getters.calendar, whereas the above
      // does not.
      Vue.set(state.calendars[state.activeCalendar], 0, state.calendars[state.activeCalendar][0]);
    },
    setActiveCalendar(state, memberId) {
      Vue.set(state, 'activeCalendar', memberId);
    },
    updateCalendarDay(state, day) {
      const calendar = state.calendars[state.activeCalendar];
      const index = indexForDate(day.date, calendar);
      const existing = state.calendars[state.activeCalendar][index];
      Vue.set(state.calendars[state.activeCalendar], index, Object.assign(existing, day));
    },
    addCalendarDay(state, day) {
      const calendar = state.calendars[state.activeCalendar];
      calendar.push(day);
      calendar.sort(calendarSort);
      Vue.set(state.calendars, state.activeCalendar, calendar);
    },
  },
  actions: {
    ...analyticsActions,
    ...plannerCalendarActions,
    ...powerScalesActions,
    clearCalendar(context) {
      context.commit('clearCalendar');
    },
    async loadCalendar(context, { memberId, cognitoId }) {
      const result = await this.$container.dao.getCalendar(memberId, cognitoId);
      context.commit('loadCalendar', {
        memberId,
        calendar: result.data,
      });
    },
    async setActiveCalendar(context, memberId) {
      if (!Object.keys(context.getters.calendars).includes(memberId)) {
        await context.dispatch('loadCalendar', {
          memberId,
          cognitoId: context.getters.rosterMember(memberId).awsCognitoSub,
        });
        await context.dispatch('loadAveragePowerScales', memberId);
      }
      context.commit('setActiveCalendar', memberId);
    },
    addWorkout(context, workout) {
      const calendar = context.getters.calendarCopy;
      const day = calendar.find(i => i.date.isSame(workout.localStartTime, 'day'));
      if (day) {
        if (!day.workouts) {
          day.workouts = [];
        }

        const existingWorkout = day.workouts.findIndex(w => w.trainingPlanId === workout.trainingPlanId);
        if (existingWorkout > -1) {
          day.plannedWeightedLoad -= day.workouts[existingWorkout].getEstimatedLoad();
          day.plannedDistance -= day.workouts[existingWorkout].totalDistance;
          day.plannedDuration -= day.workouts[existingWorkout].plannedDuration;
          day.workouts[existingWorkout] = workout;
        } else {
          day.workouts.push(workout);
          ['plannedWeightedLoad', 'plannedDistance', 'plannedDuration'].forEach((i) => {
            if (!day[i]) {
              day[i] = 0;
            }
          });
        }

        const load = workout.getEstimatedLoad();
        day.plannedWeightedLoad += load;
        day.plannedDistance += workout.getEstimatedDistance();
        day.plannedDuration += workout.getEstimatedDuration();

        context.commit('updateCalendarDay', day);
        context.dispatch('recalculateAcr', {
          date: day.date.clone(),
          includeWorkouts: true,
        });

        return;
      }

      const range = calendarDateRange(calendar);
      for (
        const m = moment(range[1].add(1, 'day'));
        m.isBefore(workout.localStartTime.endOf('day'));
        m.add(1, 'day')
      ) {
        const clone = cloneDay(calendar[0]);
        clone.date = m.clone();
        const plannedWeightedLoad = m.isSame(workout.localStartTime, 'day')
          ? workout.getEstimatedLoad()
          : 0;
        clone.plannedWeightedLoad = plannedWeightedLoad;
        clone.ebAcrPw = context.getters.estimateAcr(clone.date, plannedWeightedLoad, 'ebl');
        clone.vbAcrPw = context.getters.estimateAcr(clone.date, plannedWeightedLoad, 'vbl');
        clone.workouts = [workout];
        calendar.push(clone);
        context.commit('addCalendarDay', clone);
      }

      context.dispatch('recalculateAcr', {
        date: workout.localStartTime.clone(),
        includeWorkouts: true,
      });
    },
    deleteWorkout(context, workout) {
      const calendar = context.getters.calendarCopy;
      const day = calendar.find(i => i.date.isSame(workout.localStartTime, 'day'));
      if (day && day.workouts) {
        day.workouts.forEach((data, i) => {
          const w = new Workout(data);
          if (w.id === workout.id) {
            day.plannedWeightedLoad -= workout.getEstimatedLoad();
            day.plannedDistance -= workout.totalDistance;
            day.plannedDuration -= workout.totalDuration;
            day.workouts[i].removedOn = moment().format();
          }
        });
        context.commit('updateCalendarDay', day);
      }

      context.dispatch('recalculateAcr', {
        date: workout.localStartTime.clone(),
        includeWorkouts: true,
      });
    },
    addActivities(context, activities) {
      let earliestDate = moment();
      let hasGaps = false;
      let calendar = context.getters.calendarCopy;
      activities.map(a => new Activity(a))
        .forEach((activity) => {
          if (activity.localStartTime.isBefore(earliestDate)) {
            earliestDate = activity.localStartTime.clone().startOf('day');
          }
          let day = calendar.find(i => i.date.isSame(activity.localStartTime, 'day'));
          if (day) {
            day = addActivityToDay(day, activity);
            calendar[indexForDate(day.date, calendar)] = day;
          } else {
            day = addActivityToDay(cloneDay(calendar[0]), activity);
            calendar.push(day);
            calendar.sort(calendarSort);
            hasGaps = true;
          }
        });

      if (hasGaps) {
        calendar = fillGaps(calendar, calendar[0].date, calendar[calendar.length - 1].date);
      }

      context.dispatch('recalculateAcr', { date: earliestDate, includeWorkouts: false });
      context.dispatch('recalculateAcr', { date: earliestDate, includeWorkouts: true });
    },
    updateCalendarDay(context, day) {
      context.commit('updateCalendarDay', day);
    },
  },
};
